import { useEffect, useState } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';
import { Download, X } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

export default function PWA() {
  const { t } = useTranslation();
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);

  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegistered(r) {
      console.log('PWA SW Registered');
    },
    onRegisterError(error) {
      console.error('PWA SW registration error', error);
    },
  });

  useEffect(() => {
    let delayTimer: ReturnType<typeof setTimeout>;
    const handler = (e: any) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Delay 30s so it doesn't interrupt the first impression
      delayTimer = setTimeout(() => setShowInstallPrompt(true), 30000);
    };

    window.addEventListener('beforeinstallprompt', handler);

    const notificationHandler = () => requestNotificationPermission();
    window.addEventListener('request-pwa-notification', notificationHandler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('request-pwa-notification', notificationHandler);
      clearTimeout(delayTimer);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setShowInstallPrompt(false);
      setDeferredPrompt(null);
    }
  };

  const requestNotificationPermission = async () => {
    if (!('Notification' in window)) {
      toast.error(t('pwa.notificationsNotSupported'));
      return;
    }

    const permission = await Notification.requestPermission();
    if (permission === 'granted') {
      toast.success(t('pwa.notificationsSupported'));
    } else {
      toast.error(t('pwa.notificationsDenied'));
    }
  };

  return (
    <>
      {/* Update prompt */}
      {needRefresh && (
        <div className="fixed bottom-20 left-4 right-4 z-[100] p-4 bg-primary text-primary-foreground rounded-xl shadow-2xl flex items-center justify-between border border-white/20 animate-in fade-in slide-in-from-bottom-5">
          <div className="flex flex-col gap-1">
            <span className="text-sm font-bold">{t('pwa.updateTitle')}</span>
            <span className="text-xs opacity-90">{t('pwa.updateDesc')}</span>
          </div>
          <div className="flex gap-2">
            <button 
              onClick={() => updateServiceWorker(true)}
              className="px-3 py-1.5 bg-white text-primary text-xs font-bold rounded-lg hover:bg-opacity-90 transition-colors"
            >
              {t('pwa.updateAction')}
            </button>
            <button 
              onClick={() => setNeedRefresh(false)}
              className="p-1.5 opacity-60 hover:opacity-100 transition-opacity"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Install Prompt Banner — pinned to BOTTOM-LEFT so it never overlaps the chatbot (bottom-right) */}
      {showInstallPrompt && (
        <div className="fixed bottom-24 left-4 z-[99] w-[calc(100vw-2rem)] max-w-sm p-4 bg-secondary/95 backdrop-blur-md border border-border rounded-xl shadow-2xl flex items-center gap-4 animate-in fade-in slide-in-from-bottom-5">
          <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
            <Download className="h-6 w-6 text-primary" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-bold">{t('pwa.installTitle')}</p>
            <p className="text-xs text-muted-foreground">{t('pwa.installDesc')}</p>
          </div>
          <div className="flex flex-col gap-2">
            <button 
              onClick={handleInstall}
              className="px-3 py-1.5 bg-primary text-primary-foreground text-xs font-bold rounded-lg hover:brightness-110"
            >
              {t('pwa.installAction')}
            </button>
            <button 
              onClick={() => setShowInstallPrompt(false)}
              className="text-[10px] text-muted-foreground hover:text-foreground text-center"
            >
              {t('pwa.installLater')}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

